import { useCallback } from 'react'

import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import { Button } from '@/components/ui/button'
import { Copy } from '@/components/ui/copy/copy'
import { Input } from '@/components/ui/input'
import { PageCard, PageLayout, PageTwoColumn } from '@/components/ui/page'
import { usePrompt } from '@/components/ui/prompt'
import { Text } from '@/components/ui/text'

import { cx } from '@/libs/cva'
import { invalidateQueries } from '@/libs/helper'
import { useAuth } from '@/libs/hooks/use-auth'

import { posDeviceDetail, posDeviceRefreshToken } from '@/features/pos/api'

export const Route = createFileRoute('/_app/outlet/pos/$posid')({
	loader: async ({ context, params }) => {
		const { posid } = params
		await context.qc.ensureQueryData(
			posDeviceDetail.qry({
				id: posid,
				oId: useAuth.getState().outlet.outletId,
			})
		)
	},
	component: RouteComponent,
})

function RouteComponent() {
	return (
		<PageLayout>
			<PageLayout.Header
				breadcrumbs={[
					['/outlet', 'Outlet'],
					['/outlet/pos', 'Point of Sale'],
					[Route.fullPath, 'Detail Perangkat'],
				]}
			/>
			<PageTwoColumn>
				<PageTwoColumn.Main>
					<GeneralInfoSection />
				</PageTwoColumn.Main>
				<PageTwoColumn.Sidebar>
					<PairStatusSection />
				</PageTwoColumn.Sidebar>
			</PageTwoColumn>
		</PageLayout>
	)
}

function useData() {
	const { posid } = Route.useParams()
	const { outletId } = useAuth((s) => s.outlet)
	const { data } = useSuspenseQuery(
		posDeviceDetail.qry({ id: posid, oId: outletId })
	)
	return data
}

function GeneralInfoSection() {
	const { device } = useData()

	return (
		<PageCard>
			<PageCard.Header title="Informasi Perangkat" />
			<PageCard.ContentRow title="Nama Perangkat" value={device.name} />
			<PageCard.ContentRow title="Role" value={device.role} />
		</PageCard>
	)
}

function PairStatusSection() {
	const { outletId } = useAuth((s) => s.outlet)
	const { device } = useData()
	const { key, status } = device.pair || {}
	const { os, appVersion, model } = device.deviceInfo || {}

	const prompt = usePrompt()

	const { mutateAsync, isPending } = useMutation({
		mutationFn: async () => {
			const res = await prompt({
				title: 'Konfirmasi Refresh Token',
				description: 'Apakah Anda yakin ingin memuat ulang token ini?',
			})

			if (!res) return

			await posDeviceRefreshToken.api({
				body: { id: device.id, oId: outletId },
			})

			invalidateQueries([posDeviceDetail.key])
		},
	})

	return (
		<PageCard>
			<PageCard.Header
				title="Status Perangkat"
				actions={
					<Text
						className={cx({
							'text-ui-tag-green-text': status === 'claimed',
							'text-ui-tag-red-text': status === 'revoked',
							'text-ui-tag-orange-text': status === 'active',
						})}
					>
						{status === 'claimed' ? 'Aktif' : 'Belum dipasangkan'}
					</Text>
				}
			/>
			{key && status === 'active' && (
				<div className="flex grow px-6 py-3">
					<div className="relative">
						<Input value={key} readOnly className="w-full" />
						<Copy
							content={key}
							className="absolute right-2 top-0 translate-y-1/2"
						/>
					</div>
				</div>
			)}
			{status === 'claimed' && (
				<div className="px-6 py-3">
					<Text>IKKI POS {appVersion}</Text>
					<Text size="small" className="text-ui-tag-neutral-text">
						{model} - {os}
					</Text>
				</div>
			)}
			{status === 'claimed' && (
				<div className="px-6 py-3">
					<Button
						onClick={() => mutateAsync()}
						variant="danger"
						isLoading={isPending}
					>
						Buat Ulang Token
					</Button>
				</div>
			)}
		</PageCard>
	)
}
